import {
  AccountBalance,
  Assessment,
  Build,
  Business,
  Calculate,
  CarCrash,
  CarRental,
  CreditCard,
  Dashboard,
  FileUploadOutlined,
  FormatListBulleted,
  Group,
  Handshake,
  FlashOn,
  History,
  ListAlt,
  ChecklistOutlined,
  LocalSee,
  MenuBook,
  MessageOutlined,
  Public,
  Shield,
  LocationOn,
  Timer,
  LocalShipping,
  Handyman,
  Receipt,
  Assignment,
  Description,
  CheckCircle,
  Timeline,
  CompareArrows,
  Warehouse,
} from '@mui/icons-material'
import { Box } from '@mui/material'
import { useAuth } from 'contexts/AuthContext'
import { Navigation } from 'types/navigation'
import { UserListBuilder } from 'views/Shared'
import {
  ApplicationManagement,
  DepreciationCalculator,
  LeadManagement,
  ContactDetailPage,
  CompanyDetailPage,
  QuickLeadsPage,
  SuperAdminHome,
  UserManagement,
  VINDecoder,
  AdminFinancingHub,
  CompanyFinancingDetail,
  AdminListBuilder,
  BusinessSubmissionManagement,
  SubmissionManagement,
  PhotoUpload,
  TransporterManagement,
  AuditLog,
  MessageLog,
  PickupLocations,
  ExpenseManagement,
  InvoiceManagement,
  PerformanceReviews,
  TitleManager,
  DealApproval,
  ClientActivity,
  LTVCalculator,
  ShipmentCalculator,
  PartsManagement,
  TerminalManagement,
  TerminalDetailPage,
  InventoryV3Management,
  TimeTracker,
  PricingModels,
  CategoryPricingDetail,
  ComparisonWorkbench,
} from 'views/SuperAdmin'
import { InventoryImporterManagement } from '@/components/InventoryImporter/lazyInventoryImporterManagement'
import { isPricingCompareWorkspaceEnabled } from '@/views/SuperAdmin/Tools/GrayBook/PricingModels/comparisonFeature'
import { getDocsHomeUrl } from '@/constants/docsUrl'

const docsPublicUrl = getDocsHomeUrl()

export const getSuperUserPages = (): Navigation => {
  const { userInfo } = useAuth()
  return [
    { kind: 'header', title: 'General' },
    {
      segment: 'admin',
      title: 'Dashboard',
      icon: <Dashboard />,
      component: <SuperAdminHome />,
      slashes: ['/dashboard'],
      keywords: ['home', 'overview'],
      description: 'Open the admin dashboard',
    },
    {
      segment: 'admin/lead-management',
      title: 'Lead Management',
      icon: <Handshake />,
      component: <LeadManagement />,
      hub: 'sales',
      slashes: ['/leads', '/lead-management'],
      keywords: ['leads', 'crm', 'contacts'],
      description: 'Open lead management',
      slugBabyRoutes: [
        { pattern: 'contacts/:contactId', component: <ContactDetailPage /> },
        { pattern: 'companies/:companyId', component: <CompanyDetailPage /> },
      ],
    },
    {
      segment: 'admin/application-management',
      title: 'Financing Applications',
      icon: <AccountBalance />,
      component: <ApplicationManagement />,
      hub: 'financing',
      slashes: ['/applications'],
      keywords: ['financing', 'applications'],
      description: 'Open financing applications',
    },
    {
      segment: 'admin/financing-profiles',
      title: 'Financing Hub',
      icon: <CreditCard />,
      component: <AdminFinancingHub />,
      hub: 'financing',
      slashes: ['/financing'],
      keywords: ['financing', 'hub'],
      description: 'Open the financing hub',
      slugBabyPattern: 'company/:companyId',
      slugBabyComponent: <CompanyFinancingDetail />,
    },
    {
      segment: 'admin/management/photo-upload',
      title: 'Photo Uploader',
      icon: (
        <Box position='relative' sx={{ display: 'flex' }}>
          <LocalSee />
        </Box>
      ),
      component: <PhotoUpload />,
      hub: 'inventory',
      slashes: ['/photos'],
      keywords: ['photos', 'uploader'],
      description: 'Open photo upload',
    },
    {
      segment: 'admin/management/transporter-management',
      title: 'Transporter Management',
      icon: <LocalShipping />,
      component: <TransporterManagement />,
      hub: 'operations',
      slashes: ['/transporter'],
      keywords: ['transport', 'logistics'],
      description: 'Open transporter management',
    },
    {
      segment: 'admin/management/parts-management',
      title: 'Parts Management',
      icon: <Handyman />,
      component: <PartsManagement />,
      hub: 'operations',
      slashes: ['/parts'],
      keywords: ['parts'],
      description: 'Open parts management',
    },
    {
      segment: 'admin/user-management-v2',
      title: 'User Management',
      icon: <Group />,
      component: <UserManagement />,
      hub: 'admin',
      slashes: ['/users'],
      keywords: ['users', 'staff'],
      description: 'Open user management',
    },
    ...(userInfo?.isOwner === true
      ? [
          {
            segment: 'admin/performance-reviews',
            title: 'Performance Reviews',
            icon: <Assessment />,
            component: <PerformanceReviews />,
            hub: 'admin' as const,
            slashes: ['/reviews', '/performance'],
            keywords: ['reviews', 'performance'],
            description: 'Open performance reviews',
          },
          {
            segment: 'admin/client-activity',
            title: 'Client activity',
            icon: <Timeline />,
            component: <ClientActivity />,
            hub: 'sales' as const,
            slashes: ['/client-activity'],
            keywords: ['activity', 'clients'],
            description: 'Open client activity',
          },
        ]
      : []),
    {
      segment: 'optic-gray-book-calculator',
      title: 'Gray Book Calculator',
      icon: <Calculate />,
      component: <DepreciationCalculator />,
      hub: 'tools',
      slashes: ['/graybook'],
      keywords: ['graybook', 'pricing'],
      description: 'Open the Gray Book calculator',
    },
    ...(userInfo?.isOwner === true
      ? [
          {
            segment: 'optic-ltv-calculator',
            title: 'LTV Calculator',
            icon: <AccountBalance />,
            component: <LTVCalculator />,
            hub: 'financing' as const,
            slashes: ['/ltv'],
            keywords: ['ltv'],
            description: 'Open the LTV calculator',
          },
        ]
      : []),
    {
      segment: 'admin/location-management',
      title: 'Location Management',
      icon: <LocationOn />,
      component: <PickupLocations />,
      hub: 'operations',
      slashes: ['/locations'],
      keywords: ['locations', 'pickup'],
      description: 'Open pickup locations',
    },
    {
      segment: 'admin/terminal-management',
      title: 'Terminal Management',
      icon: <Warehouse />,
      component: <TerminalManagement />,
      hub: 'operations',
      slashes: ['/terminals'],
      keywords: ['terminals', 'warehouse'],
      description: 'Open terminals',
      slugBabyPattern: ':terminalId',
      slugBabyComponent: <TerminalDetailPage />,
    },
    {
      segment: 'admin/expense-management',
      title: 'Expense Management',
      icon: <Receipt />,
      component: <ExpenseManagement />,
      hub: 'backOffice',
      slashes: ['/expenses'],
      keywords: ['expenses'],
      description: 'Open expenses',
    },
    {
      segment: 'admin/invoice-management',
      title: 'Invoice Management',
      icon: <Description />,
      component: <InvoiceManagement />,
      hub: 'backOffice',
      slashes: ['/invoices'],
      keywords: ['invoices', 'retail'],
      description: 'Open invoices',
    },
    {
      segment: 'admin/time-tracker',
      title: 'Time Tracker',
      icon: <Timer />,
      component: <TimeTracker />,
      hub: 'backOffice',
      slashes: ['/time'],
      keywords: ['time', 'timesheet'],
      description: 'Open the time tracker',
    },
    {
      segment: 'admin/deal-approval',
      title: 'Deal Approval',
      icon: <CheckCircle />,
      component: <DealApproval />,
      hub: 'sales',
      slashes: ['/deals'],
      keywords: ['deals', 'approval'],
      description: 'Open deal approval',
    },
    { kind: 'divider' },
    { kind: 'header', title: 'Inventory' },
    {
      segment: 'admin/inventory-v3',
      title: 'Inventory',
      icon: <ListAlt />,
      component: <InventoryV3Management inventoryCollection='master_inventory' />,
      hub: 'inventory',
      slashes: ['/inventory'],
      keywords: ['inventory', 'trucks'],
      description: 'Open the inventory grid',
    },
    ...(userInfo?.role === 'admin' || userInfo?.email === 'scott@optictruckworks.com'
      ? [
          {
            segment: 'admin/shield-inventory-v3',
            title: 'Shield Inventory',
            icon: (
              <Box position='relative'>
                <Shield />
              </Box>
            ),
            component: <InventoryV3Management inventoryCollection='shield_inventory' />,
            hub: 'inventory' as const,
            slashes: ['/shield'],
            keywords: ['shield', 'inventory'],
            description: 'Open Shield inventory',
          },
        ]
      : []),
    {
      segment: 'admin/inventory-importer',
      title: 'Inventory Importer',
      icon: <FileUploadOutlined />,
      component: <InventoryImporterManagement />,
      hub: 'inventory',
      slashes: ['/importer'],
      keywords: ['importer', 'csv'],
      description: 'Open the inventory importer',
    },
    {
      segment: 'admin/title-manager',
      title: 'Title Manager',
      icon: <Assignment />,
      component: <TitleManager />,
      hub: 'inventory',
      slashes: ['/titles'],
      keywords: ['titles'],
      description: 'Open title manager',
    },
    {
      segment: 'admin/management/submission-management',
      title: 'Website Submissions',
      component: <SubmissionManagement />,
      icon: <Public />,
      hub: 'sales',
      slashes: ['/submissions'],
      keywords: ['submissions', 'website'],
      description: 'Open website submissions',
    },
    {
      segment: 'admin/business-submissions',
      title: 'Business Submissions',
      icon: <Business />,
      component: <BusinessSubmissionManagement />,
      hub: 'sales',
      slashes: ['/business-submissions'],
      keywords: ['business', 'submissions'],
      description: 'Open business submissions',
    },
    {
      segment: 'admin/list-builder',
      title: 'List Builder',
      icon: <FormatListBulleted />,
      component: <AdminListBuilder />,
      hub: 'inventory',
      slashes: ['/lists'],
      keywords: ['lists'],
      description: 'Open list builder',
    },
    {
      segment: 'admin/my-inventory-lists',
      title: 'My Inventory Lists',
      icon: <ChecklistOutlined />,
      component: <UserListBuilder />,
      hub: 'inventory',
      slashes: ['/my-lists'],
      keywords: ['lists', 'my lists'],
      description: 'Open my inventory lists',
    },
    { kind: 'divider' },
    { kind: 'header', title: 'Tools' },
    {
      segment: 'admin/tools',
      title: 'Tools',
      icon: <Build />,
      children: [
        {
          segment: 'pricing-models',
          title: 'Pricing Models',
          icon: <CarCrash />,
          component: <PricingModels />,
          hub: 'tools',
          slashes: ['/pricing'],
          keywords: ['pricing', 'models'],
          description: 'Open pricing models',
          slugBabyPattern: ':formulaId',
          slugBabyComponent: <CategoryPricingDetail />,
        },
        ...(isPricingCompareWorkspaceEnabled
          ? [
              {
                segment: 'pricing-models/compare',
                title: 'Pricing Compare',
                icon: <CompareArrows />,
                component: <ComparisonWorkbench />,
                hub: 'tools' as const,
                slashes: ['/pricing-compare'],
                keywords: ['pricing', 'compare'],
                description: 'Open pricing compare',
              },
            ]
          : []),
        {
          segment: 'shipment-calculator',
          title: 'Shipment Calculator',
          icon: <LocalShipping />,
          component: <ShipmentCalculator />,
          hub: 'tools',
          slashes: ['/shipment'],
          keywords: ['shipment', 'shipping'],
          description: 'Open shipment calculator',
        },
        {
          segment: 'vin-decoder',
          title: 'VIN Decoder',
          icon: <CarRental />,
          component: <VINDecoder />,
          hub: 'tools',
          slashes: ['/vin', '/vin-decoder'],
          keywords: ['vin', 'decode'],
          description: 'Open VIN decoder',
        },
        {
          segment: 'quick-leads',
          title: 'Quick Leads',
          icon: <FlashOn />,
          component: <QuickLeadsPage />,
          hub: 'tools',
          slashes: ['/quick-leads'],
          keywords: ['quick leads', 'summit', 'capture', 'crm'],
          description: 'Fast mobile lead capture',
        },
        {
          segment: 'audit-log',
          title: 'Audit Log',
          icon: <History />,
          component: <AuditLog />,
          hub: 'admin',
          slashes: ['/audit'],
          keywords: ['audit', 'log'],
          description: 'Open audit log',
        },
        {
          segment: 'messages',
          title: 'Messages',
          icon: <MessageOutlined />,
          component: <MessageLog />,
          hub: 'admin',
          slashes: ['/messages'],
          keywords: ['messages'],
          description: 'Open message log',
        },
      ],
    },
  ]
}
